/**
 * Sticky (pop-out) live-blog video player — DOM wiring.
 *
 * Self-gates on `.Article__YouTube .Article__YouTubeEmbed.js_podcast-youtube-embed`
 * (unique to the single-live-blog template). The iframe is NEVER re-parented
 * and its src is never touched after init — pinning is a pure CSS class toggle
 * on the embed wrapper, so playback continues uninterrupted. Logic lives in
 * tpm-lib/live-blog-sticky-video.js.
 */
import { icon } from "tpm-lib/fa-icons.js";
import {
	buildIframeSrc,
	createPinMachine,
	parsePlayerMessage,
	serializeListening,
	serializePauseCommand,
} from "tpm-lib/live-blog-sticky-video.js";

const PINNED_CLASS = "--pinned";

function initStickyVideo() {
	const embed = document.querySelector(
		".Article__YouTube .Article__YouTubeEmbed.js_podcast-youtube-embed"
	);
	if (!embed) return;
	const container = embed.closest(".Article__YouTube");
	const iframe = embed.querySelector("iframe");
	if (!container || !iframe) return;

	// The only src rewrite, pre-playback: append enablejsapi=1 (idempotent —
	// a src that already carries it is left byte-identical, no reload).
	const src = iframe.getAttribute("src");
	const nextSrc = buildIframeSrc(src);
	if (nextSrc && nextSrc !== src) iframe.setAttribute("src", nextSrc);

	const machine = createPinMachine();

	// Close button (xmark, styled after StickyAdFall26__Dismiss).
	const closeBtn = document.createElement("button");
	closeBtn.type = "button";
	closeBtn.className = "Article__YouTubeEmbed__Close";
	closeBtn.setAttribute("aria-label", "Close the pinned video player");
	closeBtn.innerHTML = `<i class="fal fa-xmark">${icon("xmark", "light")}</i>`;
	embed.appendChild(closeBtn);

	function apply() {
		const pinned = machine.pinned;
		embed.classList.toggle(PINNED_CLASS, pinned);
		container.classList.toggle(PINNED_CLASS, pinned); // spacer reservation
	}

	// Playback detection: infoDelivery postMessages from the YouTube origins.
	window.addEventListener("message", (event) => {
		const msg = parsePlayerMessage(event);
		if (!msg) return;
		machine.playerState(msg.playerState);
		apply();
	});

	// Binary in/out of view for the embed's column.
	new IntersectionObserver((entries) => {
		for (const entry of entries) machine.visibility(entry.isIntersecting);
		apply();
	}).observe(container);

	// Close: pauseVideo (no orphaned audio) + unpin; re-arms on the next play.
	closeBtn.addEventListener("click", () => {
		if (machine.dismiss()) {
			iframe.contentWindow?.postMessage(serializePauseCommand(), "*");
		}
		apply();
	});

	// Handshake so the player delivers infoDelivery events and takes commands.
	iframe.addEventListener("load", () => {
		iframe.contentWindow?.postMessage(
			serializeListening("tpm-sticky-video", "tpm-sticky-video"),
			"*"
		);
	});
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initStickyVideo);
} else {
	initStickyVideo();
}
